// RequestCache: Prevents duplicate requests and caches results with TTL
class RequestCache {
    constructor(defaultTTL = 5 * 60 * 1000) { // 5 minutes default
        this.cache = new Map();
        this.pendingRequests = new Map();
        this.defaultTTL = defaultTTL;
    }

    // Generate cache key from request parameters
    static generateKey(prefix, params) {
        return `${prefix}_${JSON.stringify(params)}`;
    }

    // Get cached data if valid
    getCached(key) {
        const cached = this.cache.get(key);
        if (!cached) {
            return null;
        }

        const now = Date.now();
        const age = now - cached.timestamp;
        if (age > cached.ttl) {
            console.log(`⏰ RequestCache: Cache expired for ${key.substring(0, 50)}... (was ${Math.round(age/1000)}s old, TTL: ${cached.ttl/1000}s)`);
            this.cache.delete(key);
            return null;
        }

        // Only log cache hits (these save us API calls)
        console.log(`✨ RequestCache: Using cached data for ${key.substring(0, 50)}... (${Math.round(age/1000)}s old)`);
        return cached.data;
    }

    // Cache data with optional custom TTL
    setCached(key, data, ttl = null) {
        const actualTTL = ttl || this.defaultTTL;
        this.cache.set(key, {
            data,
            timestamp: Date.now(),
            ttl: actualTTL
        });
        // Don't log successful caching - too noisy
    }

    // Deduplicated fetch - prevents duplicate concurrent requests
    async deduplicatedFetch(key, fetchFn, ttl = null) {
        // Check cache first
        const cached = this.getCached(key);
        if (cached !== null) {
            return cached;
        }

        // Check if request is already in progress
        if (this.pendingRequests.has(key)) {
            console.log(`🔄 RequestCache: Duplicate request prevented for ${key.substring(0, 50)}... (waiting for in-progress request)`);
            return this.pendingRequests.get(key);
        }

        // Create new request
        const requestPromise = fetchFn()
            .then(data => {
                this.setCached(key, data, ttl);
                this.pendingRequests.delete(key);
                return data;
            })
            .catch(error => {
                this.pendingRequests.delete(key);
                console.log(`❌ RequestCache: Request failed for ${key.substring(0, 50)}... - ${error.message}`);
                throw error;
            });

        this.pendingRequests.set(key, requestPromise);
        return requestPromise;
    }

    // Clear specific cache entries
    invalidate(keyPattern) {
        const keysToDelete = [];
        for (const key of this.cache.keys()) {
            if (key.includes(keyPattern)) {
                keysToDelete.push(key);
            }
        }
        keysToDelete.forEach(key => this.cache.delete(key));
    }

    // Clear all cache
    clear() {
        this.cache.clear();
        this.pendingRequests.clear();
    }

    // Get cache statistics
    getStats() {
        const now = Date.now();
        let validEntries = 0;
        let expiredEntries = 0;

        for (const [key, value] of this.cache.entries()) {
            if (now - value.timestamp <= value.ttl) {
                validEntries++;
            } else {
                expiredEntries++;
            }
        }

        return {
            totalEntries: this.cache.size,
            validEntries,
            expiredEntries,
            pendingRequests: this.pendingRequests.size
        };
    }
}

// Create singleton instance
const globalRequestCache = new RequestCache();

export { RequestCache, globalRequestCache };